<template>
  <footer class="modern-footer">
    <!-- Main Footer Content -->
    <div class="footer-main">
      <div class="footer-container">
        <!-- Brand Section -->
        <div class="footer-section brand-section">
          <div class="footer-logo">
            <img
              src="@/assets/CarenuityLogoTransparent.png"
              alt="Carenuity"
              class="logo-img"
            />
          </div>
          <p class="brand-tagline">
            Carenuity provides modular and highly customizable sensor solutions for monitoring vital signs and real estate air quality.
          </p>
        </div>

        <!-- Quick Links -->
        <div class="footer-section">
          <h3 class="footer-title">Quick Links</h3>
          <ul class="footer-links">
            <li><router-link to="/dashboard" class="footer-link">Dashboard</router-link></li>
            <li><router-link to="/alarms" class="footer-link">Alarms</router-link></li>
            <li><router-link to="/devices" class="footer-link">Devices</router-link></li>
            <li><router-link to="/settings" class="footer-link">Settings</router-link></li>
            <li><a href="https://www.chipglobe.shop/en" target="_blank" rel="noopener noreferrer" class="footer-link">Shop</a></li>
          </ul>
        </div>

        <!-- Resources -->
        <div class="footer-section">
          <h3 class="footer-title">Resources</h3>
          <ul class="footer-links">
            <li><a href="https://www.carenuity.com" target="_blank" rel="noopener noreferrer" class="footer-link">About Carenuity</a></li>
            <li><a href="https://www.carenuity.com/contact-us/" target="_blank" rel="noopener noreferrer" class="footer-link">Contact Us</a></li>
            <li><a href="https://www.carenuity.com/sq-panel/" target="_blank" rel="noopener noreferrer" class="footer-link">SQ-Panel</a></li>
            <li><a :href="germanManualUrl" target="_blank" rel="noopener noreferrer" class="footer-link" @click.stop>SQ-Panel Manual (DE)</a></li>
            <li><a :href="englishManualUrl" target="_blank" rel="noopener noreferrer" class="footer-link" @click.stop>SQ-Panel Manual (EN)</a></li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div class="footer-section">
          <h3 class="footer-title">Get in Touch</h3>
          <ul class="footer-contact">
            <li class="contact-item">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              <a href="mailto:info@carenuity.com" class="footer-link">info@carenuity.com</a>
            </li>
            <li class="contact-item">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              <a href="tel:+498912224694" class="footer-link">(+49) 89-1222469-40</a>
            </li>
            <li class="contact-item">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <span>Munich, Germany</span>
            </li>
          </ul>
        </div>

        <!-- Social Links -->
        <div class="footer-section">
          <h3 class="footer-title">Follow Us</h3>
          <div class="social-links">
            <a href="https://www.linkedin.com/company/carenuity" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="LinkedIn">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
              </svg>
            </a>
            <a href="https://twitter.com/carenuity" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="Twitter">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
              </svg>
            </a>
            <a href="https://github.com/carenuity" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="GitHub">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Bottom -->
    <div class="footer-bottom">
      <div class="footer-container">
        <div class="footer-bottom-content">
          <p class="copyright">
            &copy; {{ currentYear }} Chipglobe GmbH. All rights reserved. Carenuity is a registered trademark of Chipglobe GmbH.
          </p>
          <div class="footer-bottom-links">
            <a href="https://www.carenuity.com/privacy-policy/" target="_blank" rel="noopener noreferrer" class="bottom-link">Privacy Policy</a>
            <span class="separator">•</span>
            <a href="https://www.carenuity.com/terms-of-service/" target="_blank" rel="noopener noreferrer" class="bottom-link">Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';

/**
 * TheModernFooter Component
 *
 * Modern footer with company information, navigation links, and social media links
 * Features:
 * - Brand section with logo and tagline
 * - Quick links to internal pages
 * - Resources links to external pages
 * - Contact information
 * - Social media links (LinkedIn, Twitter, GitHub)
 * - Copyright notice with dynamic year
 * - Privacy policy and terms of service links
 * - PDF manuals for SQ-Panel (German and English)
 */
export default defineComponent({
  name: 'TheModernFooter',
  setup() {
    // Computed - Current year for copyright
    const currentYear = computed(() => new Date().getFullYear());

    // PDF manual URLs - served from public folder as static assets
    const germanManualUrl = '/assets/docs/SQ-Panel_Anleitung-zur-Inbetriebnahme_V1.0.4.pdf';
    const englishManualUrl = '/assets/docs/SQ-Panel_Getting-Started-Guide-for-Commisioning_V1.0.3e.pdf';

    return {
      currentYear,
      germanManualUrl,
      englishManualUrl
    };
  }
});
</script>

<style scoped>
/* ============================================
   Modern Footer Base
   ============================================ */
.modern-footer {
  width: 100%;
  background: linear-gradient(135deg, var(--color-text-primary) 0%, #1a252f 100%);
  color: var(--color-gray-50);
  margin-top: auto;
}

/* ============================================
   Footer Main Content
   ============================================ */
.footer-main {
  padding: var(--spacing-6) 0 var(--spacing-4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-6);
}

/* ============================================
   Footer Grid Layout
   ============================================ */
.footer-main .footer-container {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1.2fr 1fr;
  gap: var(--spacing-8);
}

/* ============================================
   Footer Sections
   ============================================ */
.footer-section {
  display: flex;
  flex-direction: column;
}

/* Brand Section */
.brand-section {
  max-width: 280px;
}

.footer-logo {
  margin-bottom: var(--spacing-3);
}

.logo-img {
  height: 36px;
  width: auto;
  transition: var(--transition-base);
}

.logo-img:hover {
  transform: scale(1.05);
  filter: drop-shadow(0 0 8px rgba(25, 135, 84, 0.4));
}

.brand-tagline {
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--color-gray-400);
  margin-bottom: 0;
}

/* Social Links */
.social-links {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.social-link {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: var(--color-gray-50);
  transition: var(--transition-base);
  text-decoration: none;
}

.social-link:hover {
  background: linear-gradient(135deg, var(--color-primary) 0%, #038144 100%);
  transform: translateY(-2px);
  box-shadow: 0 3px 10px rgba(25, 135, 84, 0.3);
}

.social-link svg {
  width: 18px;
  height: 18px;
}

/* ============================================
   Footer Titles
   ============================================ */
.footer-title {
  font-size: 0.95rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-50);
  margin-bottom: 0.875rem;
  position: relative;
  padding-bottom: 0.375rem;
}

.footer-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 30px;
  height: 2px;
  background: linear-gradient(135deg, var(--color-primary) 0%, #038144 100%);
  border-radius: 2px;
}

/* ============================================
   Footer Links
   ============================================ */
.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.footer-link {
  color: var(--color-gray-400);
  text-decoration: none;
  font-size: 0.85rem;
  transition: var(--transition-base);
  display: inline-block;
  position: relative;
  padding-left: 0;
}

.footer-link::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 2px;
  background: var(--color-primary);
  transition: width 0.3s ease;
}

.footer-link:hover {
  color: var(--color-primary);
  padding-left: 12px;
}

.footer-link:hover::before {
  width: 8px;
}

/* ============================================
   Contact Info
   ============================================ */
.footer-contact {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: var(--color-gray-400);
  font-size: 0.85rem;
}

.contact-item svg {
  width: 18px;
  height: 18px;
  color: var(--color-primary);
  flex-shrink: 0;
}

.contact-item a {
  color: var(--color-gray-400);
  text-decoration: none;
  transition: var(--transition-colors);
}

.contact-item a:hover {
  color: var(--color-primary);
}

/* ============================================
   Footer Bottom
   ============================================ */
.footer-bottom {
  padding: var(--spacing-4) 0;
  background: rgba(0, 0, 0, 0.2);
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-3);
}

.copyright {
  font-size: 0.8rem;
  color: #95a5a6;
  margin: 0;
}

.footer-bottom-links {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  flex-wrap: wrap;
}

.bottom-link {
  font-size: 0.8rem;
  color: #95a5a6;
  text-decoration: none;
  transition: var(--transition-colors);
}

.bottom-link:hover {
  color: var(--color-primary);
}

.separator {
  color: #7f8c8d;
  font-size: 0.8rem;
}

/* ============================================
   Responsive Design
   ============================================ */
@media (max-width: 1200px) {
  .footer-main .footer-container {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    gap: var(--spacing-6);
  }

  .brand-section {
    grid-column: 1 / -1;
    max-width: 100%;
    margin-bottom: var(--spacing-3);
  }
}

@media (max-width: 968px) {
  .footer-main .footer-container {
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-6);
  }

  .brand-section {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .footer-main {
    padding: var(--spacing-4) 0 var(--spacing-3);
  }

  .footer-main .footer-container {
    grid-template-columns: 1fr;
    gap: var(--spacing-5);
  }

  .footer-container {
    padding: 0 var(--spacing-4);
  }

  .brand-section {
    text-align: center;
    grid-column: 1;
    margin-bottom: 0;
  }

  .footer-logo {
    display: flex;
    justify-content: center;
  }

  .social-links {
    flex-direction: row;
    justify-content: center;
  }

  .footer-section {
    text-align: center;
  }

  .footer-title::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .footer-link::before {
    display: none;
  }

  .footer-link:hover {
    padding-left: 0;
  }

  .contact-item {
    justify-content: center;
  }

  .footer-bottom-content {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .footer-main {
    padding: var(--spacing-3) 0 var(--spacing-2);
  }

  .footer-container {
    padding: 0 var(--spacing-3);
  }

  .logo-img {
    height: 32px;
  }

  .brand-tagline {
    font-size: 0.8rem;
  }

  .footer-title {
    font-size: 0.9rem;
  }

  .footer-link,
  .contact-item {
    font-size: 0.8rem;
  }

  .social-link {
    width: 32px;
    height: 32px;
  }

  .social-link svg {
    width: 16px;
    height: 16px;
  }

  .footer-bottom {
    padding: var(--spacing-3) 0;
  }

  .copyright,
  .bottom-link {
    font-size: 0.75rem;
  }

  .footer-bottom-links {
    gap: var(--spacing-2);
  }
}
</style>
